import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { TimeSheetDay } from "../types/timesheet";

interface Props {
  chooseHours: boolean;
  handleHoursClose: () => void;
  day: TimeSheetDay;
  onSave: (hours: number, minute: number) => void;
  setBoxClicked: Dispatch<SetStateAction<boolean>>;
  originalNewHours: number | undefined;
  originalNewMins: number | undefined;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const HoursTimesheetDialog: FunctionComponent<Props> = (
  props: Props
): JSX.Element => {
  const {
    chooseHours,
    handleHoursClose,
    day,
    onSave,
    setBoxClicked,
    originalNewHours,
    originalNewMins,
  } = props;
  const [newHours, setNewHours] = useState<number>(day.hour!);
  const [newMins, setNewMins] = useState<number>(day.minute!);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);

  const listHours = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const listMins = [0, 15, 30, 45];

  const { t } = useTranslation();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleHourChange = (event: SelectChangeEvent) => {
    const newHrs = parseInt(event.target.value);
    setNewHours(newHrs);
    setIsDisabled(newHrs === originalNewHours && newMins == originalNewMins);
  };

  const handleMinsChange = (event: SelectChangeEvent) => {
    const newMinutes = parseInt(event.target.value);
    setNewMins(newMinutes);
    setIsDisabled(
      newHours === originalNewHours && newMinutes === originalNewMins
    );
  };

  const handleSaveHours = () => {
    onSave(newHours, newMins);

    if (newHours === day.hour && newMins === day.minute) {
      setBoxClicked(false);
    } else {
      setBoxClicked(true);
    }
    handleHoursClose();
    setIsDisabled(true);
  };

  return (
    <BootstrapDialog
      onClose={() => {
        handleHoursClose();
        setNewHours(originalNewHours!);
        setNewMins(originalNewMins!);
      }}
      open={chooseHours}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {t("dialogs.hoursDialog.title")}
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={() => {
          handleHoursClose();
          setNewHours(originalNewHours!);
          setNewMins(originalNewMins!);
        }}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <FormControl sx={{ m: 1, minWidth: isSmallScreen ? "150px" : "200px" }}>
          <InputLabel> {t("dialogs.hoursDialog.hours")} </InputLabel>
          <Select
            size={isSmallScreen ? "small" : "medium"}
            value={newHours !== undefined ? newHours.toString() : ""}
            label={t("dialogs.hoursDialog.hours")}
            onChange={handleHourChange}
          >
            {listHours.map((hr, index) => (
              <MenuItem key={index} value={hr.toString()}>
                {" "}
                {hr}{" "}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ m: 1, minWidth: isSmallScreen ? "150px" : "200px" }}>
          <InputLabel> {t("dialogs.hoursDialog.minutes")} </InputLabel>
          <Select
            size={isSmallScreen ? "small" : "medium"}
            value={newMins !== undefined ? newMins.toString() : ""}
            label={t("dialogs.hoursDialog.minutes")}
            onChange={handleMinsChange}
          >
            {listMins.map((min, index) => (
              <MenuItem key={index} value={min.toString()}>
                {" "}
                {min}{" "}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button
          autoFocus
          onClick={() => {
            handleHoursClose();
            setNewHours(originalNewHours!);
            setNewMins(originalNewMins!);
          }}
        >
          {t("buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveHours}
          disabled={isDisabled}
        >
          {t("buttons.save")}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
};

export default HoursTimesheetDialog;
